import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
    DEFAULT_INTERFACE_LANG,
    normalizeLangParam,
    SCREENSHOT_LANG_ISO,
    screenshotLangQueryValue,
} from "./interfaceLang";
import { setInterfaceLanguage } from "../services/LangPrefs";
import {
    DEFAULT_LANG,
    getCachedMessages,
    translateFromMessages,
    usePagesTranslation,
} from "../pages/locales";
import { applyScreenshotDocumentLang } from "./screenshotFonts";

/** Short ISO codes for `?lang=` (legacy). Prefer {@link screenshotLangQueryValue} for App Store tags. */
export const SCREENSHOT_LANG_CODES = SCREENSHOT_LANG_ISO;

export function langFromScreenshotCode(code) {
    return normalizeLangParam(code);
}

export function screenshotLangCode(lang) {
    return screenshotLangQueryValue(lang);
}

/** Always sets `?lang=` (including English) so language switches clear the previous locale. */
export function appendScreenshotLang(path, lang, device) {
    const code = screenshotLangQueryValue(lang || DEFAULT_INTERFACE_LANG) || "en-US";
    const raw = typeof path === "string" ? path : "";
    const qIndex = raw.indexOf("?");
    const base = qIndex >= 0 ? raw.slice(0, qIndex) : raw;
    const existing = qIndex >= 0 ? raw.slice(qIndex + 1) : "";
    const params = new URLSearchParams(existing);
    params.set("lang", code);
    if (device) params.set("device", device);
    return `${base}?${params.toString()}`;
}

/** Apply `?lang=` from screenshot / snapshot URLs to `ui.lang` before first render. */
export function applyScreenshotLangFromQuery(search) {
    try {
        const q =
            search instanceof URLSearchParams
                ? search
                : new URLSearchParams(
                      typeof search === "string"
                          ? search.startsWith("?")
                              ? search.slice(1)
                              : search
                          : typeof window !== "undefined"
                            ? window.location.search
                            : "",
                  );
        const lang = normalizeLangParam(q.get("lang"));
        if (lang) {
            setInterfaceLanguage(lang);
            applyScreenshotDocumentLang(lang);
        }
    } catch {
        /* ignore */
    }
}

export function screenshotSceneKey(app, slug, field) {
    return `mobileScreenshot.${app}.scenes.${slug}.${field}`;
}

export function slidingAnimationKey(slug, field) {
    return `mobileScreenshot.sliding.animation.${slug}.${field}`;
}

/**
 * Screenshot / animation pages — honors `?lang=` on the URL, falls back to English copy.
 */
export function useScreenshotTranslation() {
    const [searchParams] = useSearchParams();
    const urlLang = normalizeLangParam(searchParams.get("lang"));
    const resolvedLang = urlLang || DEFAULT_INTERFACE_LANG;
    const { t: tLang, lang, loading, setLang } = usePagesTranslation(resolvedLang);

    useEffect(() => {
        setLang(resolvedLang);
        applyScreenshotDocumentLang(resolvedLang);
        setInterfaceLanguage(resolvedLang);
    }, [resolvedLang, setLang]);

    const t = useCallback(
        (key, params) => {
            const val = tLang(key, params);
            if (val !== key) return val;
            const enMessages = getCachedMessages(DEFAULT_LANG);
            const fallback = translateFromMessages(enMessages, key, params);
            if (fallback !== key) return fallback;
            if (typeof params === "string") return params;
            return val;
        },
        [tLang],
    );

    return { t, lang, loading, setLang, langCode: screenshotLangCode(lang) };
}
